/**
 * previewWarningCopy -- translate composite-preview warning codes into copy an
 * operator can act on. GH #2874.
 *
 * THE BUG THIS CLOSES
 * -------------------
 * When two decorations resolve to the same garment view (say a left-chest and
 * a left-sleeve mark on a SKU whose Artifi views do not separate the sleeve),
 * the backend DROPS one placement and records why:
 *
 *   composite_preview_render.py:391  warnings.append(f"placement_view_collision:{zone}")
 *
 * The API returns those codes (`artifi_preview.py`, the "warnings" key) and
 * both preview composables expose them as a `warnings` ref -- but until this
 * module landed NO component read the array. The operator saw a preview that
 * was silently missing a decoration and could not tell it from a correct one.
 * That is what was reported as decorations "randomly moving".
 *
 * WHAT THIS MODULE IS
 * -------------------
 * A pure mapping: machine codes in, one human sentence + a severity out. It
 * deliberately owns NO Vue state -- the component decides where to draw the
 * indication, this decides what it says. Keeping the copy here (rather than in
 * a template) is what lets the unknown-code fallback be asserted by test.
 *
 * COPY RULES (follow these when adding a code)
 * --------------------------------------------
 *  1. Describe the LIMITATION, never a user error. The operator did nothing
 *     wrong: the garment simply has no separate view for that zone. Words like
 *     "error", "invalid" and "failed" are wrong here and are asserted against.
 *  2. Say the decoration is still on the product. Only the PICTURE is short a
 *     mark -- the order is unaffected. This mirrors the voice the product
 *     editor's Stage fallback already uses ("Only the mockup image is
 *     affected", Stage.vue's _renderFailedMessage).
 *  3. NEVER let a raw code reach a user. Anything unmapped -- including a code
 *     a future backend adds -- degrades to honest generic copy.
 *
 * The last rule is why the unknown branch does not fall back to
 * `placementZoneToLabel`: that helper returns the ORIGINAL code uppercased for
 * an unknown zone, which would put "PLACEMENT_GREMLIN_ATE_IT" on screen. We
 * use it only for zones we recognise and drop the zone from the sentence
 * otherwise.
 */
import { placementZoneToLabel, PLACEMENT_ZONE_LABELS } from "@/utils/placementZoneToLabel";

/**
 * Severity is about how badly the picture misrepresents the product, NOT about
 * blame. Both values are informational for a screen reader (see the component:
 * role="status", never role="alert") -- BARE simply means the image shows no
 * decoration at all, which is the state most easily mistaken for a correct
 * preview of a blank garment.
 */
export const PREVIEW_WARNING_SEVERITY = Object.freeze({
    INFO: "info",
    BARE: "bare",
});

// The bare-garment marker. composite_preview_render.py appends this when every
// drawable candidate dropped, so the served image is the undecorated garment.
const BARE_FALLBACK_CODE = "composite_no_survivors_bare_fallback";

/**
 * Codes that are real but NOT worth an operator's attention. "stale" is a
 * cache-freshness signal from the Artifi client: the image is the CORRECT
 * decoration, just re-served from cache. Surfacing it would put a badge on
 * healthy previews and train people to ignore the badge on the ones that
 * matter.
 */
const IGNORED_CODES = new Set(["stale"]);

/**
 * Per-reason phrasing for a dropped placement. Each takes the human zone label
 * (possibly empty) and returns a clause describing why that mark is absent.
 * Kept as functions so a zone-less variant reads naturally.
 */
const DROP_REASONS = Object.freeze({
    placement_view_collision: (zone) =>
        zone
            ? `this garment has no separate view for the ${zone.toLowerCase()}, so that decoration can't be shown here`
            : "this garment has no separate view for one of the placements, so that decoration can't be shown here",
    placement_no_zone_box: (zone) =>
        zone
            ? `this garment has no mapped ${zone.toLowerCase()} area, so that decoration can't be shown`
            : "one placement has no mapped area on this garment, so that decoration can't be shown",
    placement_no_artwork: (zone) =>
        zone
            ? `the ${zone.toLowerCase()} placement has no artwork attached yet`
            : "one placement has no artwork attached yet",
    placement_unmintable: (zone) =>
        zone
            ? `the ${zone.toLowerCase()} artwork isn't ready for preview yet`
            : "one placement's artwork isn't ready for preview yet",
    // EPS, or an .ai saved without PDF content: no preview is coming, so do
    // not say "yet" (composite_preview_render._unmintable_warning).
    placement_no_preview_file_type: (zone) =>
        zone
            ? `there's no preview for the ${zone.toLowerCase()} artwork's file type`
            : "there's no preview for one placement's artwork file type",
});

/**
 * The warning codes that mean "this image is MISSING a placement it should
 * show" -- a genuine drop, as opposed to "stale" (right image, served from
 * cache). Mirrors the backend rule in
 * ``decorated_preview_render.composite_dropped_a_placement``: the PREFIX
 * ``placement_`` is the signal, not an enumerated list, so a drop reason the
 * backend adds later fails CLOSED here too (held back, never shown as a
 * finished render). The bare-fallback marker is included for the same reason:
 * it means the image carries no decoration at all.
 *
 * Why callers need this (#312): the composite endpoint answers 200 with the
 * placements it COULD draw plus these codes. A caller that reads only `url`
 * mistakes a partial image for the finished one.
 *
 * @param {string[]|null|undefined} warnings raw codes from the preview API
 * @returns {string[]} the dropped-placement codes, de-duplicated, in order
 */
export function droppedPlacementCodes(warnings) {
    if (!Array.isArray(warnings)) return [];
    const out = [];
    for (const w of warnings) {
        const code = String(w ?? "");
        const isDrop =
            code.startsWith("placement_") || code === BARE_FALLBACK_CODE;
        if (isDrop && !out.includes(code)) out.push(code);
    }
    return out;
}

/**
 * Split "placement_view_collision:left_sleeve" into its parts. A code with no
 * colon (the bare marker, "stale", anything new) yields a null zone.
 */
function parseCode(code) {
    const raw = String(code ?? "");
    const idx = raw.indexOf(":");
    if (idx === -1) return { reason: raw, zoneSlug: null };
    return { reason: raw.slice(0, idx), zoneSlug: raw.slice(idx + 1) };
}

/**
 * Human label for a zone slug, or "" when we do not recognise it.
 *
 * placementZoneToLabel returns the uppercased raw code for an unknown zone,
 * which is exactly the leak rule 3 forbids -- so we check membership first and
 * return "" rather than passing an unknown slug through. Callers phrase a
 * zone-less sentence in that case.
 */
function humanZone(zoneSlug) {
    if (!zoneSlug) return "";
    const upper = String(zoneSlug).toUpperCase();
    if (!Object.prototype.hasOwnProperty.call(PLACEMENT_ZONE_LABELS, upper)) {
        return "";
    }
    return placementZoneToLabel(zoneSlug);
}

// Sentence used for anything we cannot map. Honest about the effect (a mark is
// missing from the image) without inventing a cause we do not know.
const GENERIC_CLAUSE =
    "part of this design can't be shown on this garment preview";

// Appended to every message. The reassurance is the point: the decoration is
// still on the product, only the picture is short a mark.
const STILL_ORDERED_SUFFIX =
    "The decoration is still part of the product -- only the preview is affected.";

/**
 * Describe a preview's warning codes.
 *
 * @param {string[]|null|undefined} warnings raw codes from the preview API
 * @returns {{message: string, severity: string, count: number}|null}
 *   null when there is nothing an operator should be told (the healthy case).
 */
export function describePreviewWarnings(warnings) {
    if (!Array.isArray(warnings) || warnings.length === 0) return null;

    // De-duplicate first: the count is a count of DISTINCT problems, and the
    // same (reason, zone) pair repeating is one problem reported twice.
    const distinct = [...new Set(warnings.map((w) => String(w ?? "")))].filter(
        (code) => code !== "" && !IGNORED_CODES.has(code),
    );
    if (distinct.length === 0) return null;

    const isBare = distinct.includes(BARE_FALLBACK_CODE);
    const dropCodes = distinct.filter((c) => c !== BARE_FALLBACK_CODE);

    // BARE outranks everything: the served image carries NO decoration, so the
    // headline must say so before any per-zone detail. Anything else would let
    // an operator read "left sleeve can't be shown" and assume the rest of the
    // design IS on screen when nothing is.
    if (isBare) {
        return {
            message:
                "This garment is shown plain, without any of the design -- " +
                `none of the placements could be previewed on it. ${STILL_ORDERED_SUFFIX}`,
            severity: PREVIEW_WARNING_SEVERITY.BARE,
            // The bare marker is itself one of the problems being reported.
            count: distinct.length,
        };
    }

    const clauses = dropCodes.map((code) => {
        const { reason, zoneSlug } = parseCode(code);
        const zone = humanZone(zoneSlug);
        const phrase = DROP_REASONS[reason];
        return phrase ? phrase(zone) : GENERIC_CLAUSE;
    });

    // Capitalise the first clause and join the rest, so multiple dropped zones
    // each get named rather than only the first.
    const joined = clauses.join("; ");
    const message = `${joined.charAt(0).toUpperCase()}${joined.slice(1)}. ${STILL_ORDERED_SUFFIX}`;

    return {
        message,
        severity: PREVIEW_WARNING_SEVERITY.INFO,
        count: dropCodes.length,
    };
}
